import Spinner from '@rio-cloud/rio-uikit/Spinner';
import { useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import { Navigate, Route, Routes } from 'react-router';

import { useMaintenanceModeEnabled } from '../../../configuration/featureToggle/hooks/useMaintenanceModeEnabled';
import type { RootDispatch, RootState } from '../../../configuration/setup/store';
import { getHasError, getIsLoading } from '../api/redux/api.redux';
import { ApiCalls } from '../api/redux/types';
import LoadingErrorStateWithReload from '../common/error/LoadingErrorStateWithReload';
import { UnauthorizedDialog } from '../common/error/UnauthorizedDialog';
import { fetchPermissionsThunk } from '../common/permissions/permissions.thunk';
import { getPermissions } from '../common/permissions/redux/permissions.redux';
import { getCustomerCenterPath } from '../common/routes';
import { AssetOverviewContainer } from './assetOverview/AssetOverviewContainer';
import { BookingOverviewContainer } from './bookingsOverview/BookingOverviewContainer';
import { CompanySettingsTabContainer } from './companySettings/CompanySettingsTab';
import { ContractPageContainer } from './contracts/ContractPageContainer';
import { getAllVisibleCustomerCenterTabs, getDefaultCustomerCenterTab } from './customerCenterTabService';
import { FreeTrialOverviewContainer } from './freeTrial/FreeTrialOverviewContainer';
import { HistoryContainer } from './history/HistoryContainer';
import { InboundConnectionsTab } from './inbound/InboundConnectionsTab';
import { InvoicesContainer } from './invoices/InvoicesContainer';
import { PaymentMethodsContainer } from './PaymentMethodsContainer';
import { PartnersContainer } from './partners/PartnersContainer';
import { ServiceOverviewContainer } from './serviceOverview/ServiceOverviewContainer';
import { CustomerCenterTab, type TabInfo } from './types';
import { Usage } from './usage/Usage';

interface Props {
    permissions?: string[];
    hasError: boolean;
    isLoading: boolean;
    fetchPermissions: () => void;
    defaultTab: CustomerCenterTab | undefined;
    visibleTabs: TabInfo[];
}

export function CustomerCenter(props: Props) {
    const { fetchPermissions, isLoading, hasError } = props;

    const maintenanceMode = useMaintenanceModeEnabled();

    const visibleTabs = maintenanceMode ? [] : props.visibleTabs;
    const defaultTab = maintenanceMode ? undefined : props.defaultTab;

    useEffect(() => {
        fetchPermissions();
    }, [fetchPermissions]);
    if (isLoading) {
        return <Spinner text={<FormattedMessage id={'intl-msg:marketplace.loading'} />} isInverse={false} />;
    }

    if (hasError) {
        return (
            <LoadingErrorStateWithReload
                headline={'intl-msg:marketplace.customerCenter.loading.error'}
                onReload={fetchPermissions}
            />
        );
    }

    if (visibleTabs.length <= 0 || !defaultTab) {
        return <UnauthorizedDialog />;
    }

    const containsFreeTrial = visibleTabs.some(tab => tab.componentName === CustomerCenterTab.FREE_TRIAL);
    const bookingTab = containsFreeTrial ? CustomerCenterTab.FREE_TRIAL : CustomerCenterTab.SERVICES;

    return (
        <div className={'CustomerCenter'}>
            <Routes>
                <Route path={CustomerCenterTab.SERVICES} element={<ServiceOverviewContainer />} />
                <Route path={CustomerCenterTab.ASSETS} element={<AssetOverviewContainer />} />
                <Route path={CustomerCenterTab.CONTRACTS} element={<ContractPageContainer />} />
                <Route path={CustomerCenterTab.BOOKINGS_OVERVIEW} element={<BookingOverviewContainer />} />
                <Route path={CustomerCenterTab.INVOICES} element={<InvoicesContainer />} />
                <Route path={CustomerCenterTab.HISTORY} element={<HistoryContainer />} />
                <Route path={CustomerCenterTab.PAYMENT_METHODS} element={<PaymentMethodsContainer />} />
                <Route path={CustomerCenterTab.USAGE} element={<Usage />} />
                <Route path={`billing/${CustomerCenterTab.INVOICES}`} element={<InvoicesContainer />} />
                <Route path={`billing/${CustomerCenterTab.USAGE}`} element={<Usage />} />
                <Route path='billing/payment-method' element={<PaymentMethodsContainer />} />
                <Route path={CustomerCenterTab.COMPANY_SETTINGS} element={<CompanySettingsTabContainer />} />
                <Route
                    path={CustomerCenterTab.PARTNERS}
                    element={<Navigate replace to={getCustomerCenterPath(CustomerCenterTab.INBOUND)} />}
                />
                <Route path={`${CustomerCenterTab.INBOUND}/*`} element={<InboundConnectionsTab />} />
                <Route path={CustomerCenterTab.OUTBOUND} element={<PartnersContainer />} />
                <Route path={CustomerCenterTab.FREE_TRIAL} element={<FreeTrialOverviewContainer />} />
                <Route
                    path={CustomerCenterTab.BILLING}
                    element={<Navigate replace to={getCustomerCenterPath(CustomerCenterTab.BILLING_INVOICES)} />}
                />
                <Route
                    path={CustomerCenterTab.BOOKINGS}
                    element={<Navigate replace to={getCustomerCenterPath(bookingTab)} />}
                />
                <Route path='/' element={<Navigate replace to={getCustomerCenterPath(defaultTab)} />} />
            </Routes>
        </div>
    );
}

export function mapStateToProps(state: RootState) {
    return {
        permissions: getPermissions(state),
        isLoading: getIsLoading(state, ApiCalls.PERMISSIONS),
        hasError: getHasError(state, ApiCalls.PERMISSIONS),
        visibleTabs: getAllVisibleCustomerCenterTabs(state),
        defaultTab: getDefaultCustomerCenterTab(state),
    };
}

export function mapDispatchToProps(dispatch: RootDispatch) {
    return {
        fetchPermissions: () => dispatch(fetchPermissionsThunk),
    };
}

export const CustomerCenterContainer = connect(mapStateToProps, mapDispatchToProps)(CustomerCenter);
